import React, { useState, useEffect } from 'react';
import { Gift } from "@phosphor-icons/react";
import { api } from "../../../services/api";
import { useAppStore } from "../../../store";
import { actionLogNewAvatar } from "../../../services/actionlog.service";
import { playerService } from '../../../services/avatar-challenge.service';
import { set } from 'date-fns';

interface NewAvatarPopupProps {
  isOpen: boolean;
  onClose: () => void;
  skillsData?: any;
  motivationsData?: any;
  knowledgesData?: any;
}

interface Attributes{
  playerId: string;
  actionId: string;
  attributes: {
    skills: number;
    motivations: number;
    knowledges: number;
  };
}

export default function NewAvatarPopup({ isOpen, onClose, skillsData, motivationsData, knowledgesData }: NewAvatarPopupProps) {
  const { user, refreshUser } = useAppStore();
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState<Attributes>({
    playerId: user?._id || '',
    actionId: 'new_avatar',
    attributes: {
      skills: 0,
      motivations: 0,
      knowledges: 0,
    },
  });
  const [dataPoints, setDataPoints] = useState<any | null>(null);
  const pointsList = dataPoints ? (Array.isArray(dataPoints) ? dataPoints : [dataPoints]) : [];

  useEffect(() => {
    const loadPoints = async () => {
      try {
        if (!isOpen) return;
        const pts = await playerService.getPoints();
        setDataPoints(pts);
      } catch (e) {
      }
    };

    loadPoints();
  }, [isOpen]);

  const markNewAvatarAsSeen = async () => {
    try {
      await playerService.setPlayerExtra(`${user?._id}`, {newavatar: true});

      console.log('id so do usuario:', user?._id);
      
    } catch (error) {
      console.error('Erro ao marcar newavatar como visto:', error);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    const sumTotals = (list: { total: number }[] = []) =>
      Math.abs((list || []).reduce((sum, item) => sum + (item?.total || 0), 0));

    const totalSkills = sumTotals(skillsData as any[]);
    const totalMotivations = sumTotals(motivationsData as any[]);
    const totalKnowledges = sumTotals(knowledgesData as any[]);
    setPayload((prev) => ({...prev, attributes: {
      ...prev.attributes,
      skills: totalSkills,
      motivations: totalMotivations,
      knowledges: totalKnowledges,
    }}));

  }, [isOpen, skillsData, motivationsData, knowledgesData]);

  const handleClaimReward = async () => {
    if (!user) return;

    try {
      setLoading(true);

      await actionLogNewAvatar(payload);

      await markNewAvatarAsSeen();

      await refreshUser();

      window.location.href = '/edit-avatar';
      
      onClose();
    } catch (error) {
      console.error('Erro ao resgatar recompensa do novo avatar:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="absolute inset-0 bg-black/60" />
      
      <div className="relative bg-white rounded-xl p-3 sm:p-6 max-w-md w-full my-auto z-10 text-center shadow-xl max-h-[95vh] overflow-y-auto">
        <div className="mb-2 sm:mb-4 flex justify-center">
          <div className="bg-gradient-to-br from-orange-400 to-orange-600 rounded-full p-2 sm:p-4">
            <Gift size={32} className="text-white sm:w-12 sm:h-12" />
          </div>
        </div>

        <h2 className="text-lg sm:text-2xl font-bold text-blue-600 mb-1 sm:mb-2">
          ¡Nuevo Avatar!
        </h2>


        <p className="text-gray-700 mb-3 sm:mb-6 leading-relaxed font-semibold text-sm sm:text-xl">
          ¡Nueva tienda de avatares disponible! Recupera tus gemas y personaliza tu look.
        </p>
        <div className="grid grid-cols-3 gap-2 sm:gap-6 p-2 sm:p-6">
          {['knowledge', 'skill', 'motivation'].map((cat) => {
            const item = pointsList.find((p: any) => p?.category?.toLowerCase() === cat);
            const imgUrl = item?.image?.small?.url ?? item?.image?.original?.url;

            const LABELS: Record<string, string> = {
              knowledge: 'Conocimento',
              skill: 'Habilidades',
              motivation: 'Motivacion'
            };

            const itemCategoryKey = (item?.category || cat || '').toString().toLowerCase();
            const displayLabel = LABELS[itemCategoryKey] ?? LABELS[cat] ?? item?.category ?? cat;

            const categoryToAttributeKey: Record<string, keyof typeof payload.attributes> = {
              skill: 'skills',
              motivation: 'motivations',
              knowledge: 'knowledges',
            };

            const attributeKey = categoryToAttributeKey[cat];
            const valueToShow = payload.attributes[attributeKey] ?? 0;

            return (
              <div
                key={cat}
                className="flex flex-col items-center border-gray-300 border rounded-lg sm:rounded-2xl p-1.5 sm:p-4"
              >
                {imgUrl ? (
                  <img src={imgUrl} alt={item?.category ?? cat} className="w-5 h-5 sm:w-8 sm:h-8 object-contain mb-0.5 sm:mb-2" />
                ) : (
                  <div className="w-5 h-5 sm:w-8 sm:h-8 bg-gray-200 rounded mb-0.5 sm:mb-2" />
                )}
                <span className="text-black font-semibold capitalize text-[10px] sm:text-base leading-tight">{displayLabel}</span>
                <p className='font-semibold text-xs sm:text-base' >{valueToShow}</p>
              </div>
            );
          })}
        </div>

        <button
          onClick={handleClaimReward}
          disabled={loading}
          className="w-full mt-2 sm:mt-0 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 
                     text-white font-semibold py-2.5 sm:py-3 px-4 sm:px-6 rounded-lg transition-all duration-200 
                     disabled:opacity-50 disabled:cursor-not-allowed shadow-lg text-sm sm:text-base"
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white mr-2"></div>
              Rescatando...
            </div>
          ) : (
            "Recuperar Gemas"
          )}
        </button>
      </div>
    </div>
  );
}
